import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbContextMenuModule,
  NbDialogModule,
  NbDialogService,
  NbFormFieldModule,
  NbInputModule,
  NbIconModule,
  NbMenuItem,
  NbMenuService,
  NbPosition,
  NbToastrService,
} from '@nebular/theme';
import { Subject, Subscription } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { Turma } from '../../core/api/turma';
import { TurmaService } from '../../core/api/turma.service';
import { TurmaFormComponent } from './turma-form/turma-form.component';
import { TurmaDetalhesDialogComponent } from './turma-detalhes-dialog/turma-detalhes-dialog.component';
import { formatarHoraSomenteHm } from '../../utils/formatar-hora-hm';
import { mensagemDeErro } from '../../utils/mensagem-erro';
import { ListaVaziaComponent } from '../../shared/lista-vazia/lista-vazia.component';

import { BarraCarregandoComponent } from '../../shared/barra-carregando/barra-carregando.component';
import { Ordenacao, OrdenavelComponent, paraParametroSort } from '../../shared/ordenavel/ordenavel.component';
@Component({
  selector: 'app-turma',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbContextMenuModule,
    NbDialogModule,
    NbFormFieldModule,
    NbInputModule,
    NbIconModule,
    ListaVaziaComponent,
    BarraCarregandoComponent,
    OrdenavelComponent,
  ],
  templateUrl: './turma.component.html',
  styleUrls: ['./turma.component.scss'],
})
export class TurmaComponent implements OnInit, OnDestroy {
  /**
   * `TAB-01` — ordenação **no servidor**, porque a listagem é paginada pela API.
   * O estado inicial espelha o padrão do contrato: nome ascendente.
   */
  ordenacao: Ordenacao | null = { campo: 'nome', direcao: 'asc' };

  ordenarPor(o: Ordenacao): void {
    this.ordenacao = o;
    this.page = 1;
    this.carregarTurmas();
  }

  protected get sort(): string | null {
    return paraParametroSort(this.ordenacao);
  }

  turmas: Turma[] = [];
  page = 1;
  size = 10;
  totalPages = 0;
  carregando = false;
  filtro = '';

  /** `TAB-12` — separa "não há turma" de "o recorte atual não casou com nenhuma". */
  get temFiltro(): boolean {
    return !!this.filtro.trim();
  }

  limparFiltro(): void {
    this.filtro = '';
    this.page = 1;
    this.carregarTurmas();
  }
  /** Id da turma cuja exportação Excel está em andamento (desabilita só esse botão) */
  exportandoTurmaId: string | null = null;

  private readonly destroy$ = new Subject<void>();

  readonly contextMenuTag = 'turma-acoes';
  readonly menuPlacementBottomEnd = NbPosition.BOTTOM_END;
  readonly formatarHoraHm = formatarHoraSomenteHm;

  /**
   * Referências estáveis para [nbContextMenu]; um array novo a cada CD faz o Nebular
   * chamar rebuild() no overlay e o menu deixa de abrir / de processar cliques.
   */
  private readonly acoesMenuByTurmaId = new Map<string, NbMenuItem[]>();

  constructor(
    private turmaService: TurmaService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private menuService: NbMenuService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.carregarTurmas();

    this.menuService
      .onItemClick()
      .pipe(
        filter(({ tag }) => tag === this.contextMenuTag),
        takeUntil(this.destroy$),
      )
      .subscribe(({ item }) => {
        if (item.group) {
          return;
        }
        const payload = item.data as
          | { action: string; turmaId?: string; rowIndex?: number }
          | undefined;
        if (!payload?.action) return;
        const turma = this.resolverTurmaMenu(payload);
        if (!turma) return;
        const { action } = payload;
        const id = turma.id?.trim();
        if (action === 'excel' && id && this.exportandoTurmaId === id) {
          return;
        }
        switch (action) {
          case 'detalhes':
            this.abrirDetalhes(turma);
            break;
          case 'frequencia':
            if (!id) {
              this.toastr.warning('Turma sem identificador.', 'Frequência');
              return;
            }
            void this.router.navigate(['/frequencia', id]);
            break;
          case 'faltasConsolidado':
            if (!id) {
              this.toastr.warning('Turma sem identificador.', 'Faltas consolidadas');
              return;
            }
            void this.router.navigate(['/frequencia', id, 'faltas-consolidado']);
            break;
          case 'matriz':
            if (!id) {
              this.toastr.warning('Turma sem identificador.', 'Matriz de frequência');
              return;
            }
            void this.router.navigate(['/frequencia', id, 'matriz']);
            break;
          case 'inscricao':
            if (!id) {
              this.toastr.warning('Turma sem identificador.', 'Inscrição');
              return;
            }
            void this.router.navigate(['/inscricao', id]);
            break;
          case 'excel':
            this.baixarExcelMatriculas(turma);
            break;
          case 'editar':
            this.editar(turma);
            break;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Itens do menu de ações (ícone + título) por linha — mesma referência enquanto id + export não mudam. */
  acoesContextMenu(turma: Turma, rowIndex: number): NbMenuItem[] {
    const id = turma.id?.trim();
    if (!id) {
      return this.montarItensAcoesMenu(turma, rowIndex);
    }
    const exportKey = this.exportandoTurmaId === id ? '1' : '0';
    const key = `${id}|${exportKey}`;
    let cached = this.acoesMenuByTurmaId.get(key);
    if (!cached) {
      cached = this.montarItensAcoesMenu(turma, rowIndex);
      this.acoesMenuByTurmaId.set(key, cached);
    }
    return cached;
  }

  private montarItensAcoesMenu(turma: Turma, rowIndex: number): NbMenuItem[] {
    const id = turma.id?.trim();
    const semId = !id;
    const exportando = id != null && this.exportandoTurmaId === id;
    const dataBase = { turmaId: id, rowIndex };
    return [
      { title: 'Ações', group: true },
      {
        title: 'Ver detalhes da turma',
        icon: 'info-outline',
        data: { action: 'detalhes', ...dataBase },
        hidden: semId,
      },
      {
        title: 'Frequência',
        icon: 'calendar-outline',
        data: { action: 'frequencia', ...dataBase },
        hidden: semId,
      },
      {
        title: 'Faltas consolidadas',
        icon: 'bar-chart-outline',
        data: { action: 'faltasConsolidado', ...dataBase },
        hidden: semId,
      },
      {
        title: 'Matriz de frequência',
        icon: 'grid-outline',
        data: { action: 'matriz', ...dataBase },
        hidden: semId,
      },
      {
        title: 'Inscrição pública',
        icon: 'globe-outline',
        data: { action: 'inscricao', ...dataBase },
        hidden: semId,
      },
      {
        title: exportando ? 'Exportando planilha…' : 'Exportar matrículas (Excel)',
        icon: 'file-text-outline',
        data: { action: 'excel', ...dataBase },
        hidden: semId,
      },
      {
        title: 'Editar turma',
        icon: 'edit-outline',
        data: { action: 'editar', ...dataBase },
      },
    ];
  }

  private resolverTurmaMenu(payload: {
    turmaId?: string;
    rowIndex?: number;
  }): Turma | undefined {
    const tid = payload.turmaId?.trim();
    if (tid) {
      return this.turmas.find((t) => t.id?.trim() === tid);
    }
    const ix = payload.rowIndex;
    if (ix != null && ix >= 0 && ix < this.turmas.length) {
      return this.turmas[ix];
    }
    return undefined;
  }

  /** `FIL-01` — o filtro vale quando a pessoa manda: Enter ou botão, nunca ao digitar. */
  buscarAgora(): void {
    this.page = 1;
    this.carregarTurmas();
  }

  pesquisar(): void {
    this.buscarAgora();
  }

  carregarTurmas(): void {
    this.carregando = true;
    this.turmaService
      .listar(this.page - 1, this.size, this.filtro, this.sort)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
      next: (res) => {
        this.acoesMenuByTurmaId.clear();
        this.turmas = res.content ?? [];
        this.totalPages = res.totalPages ?? 0;
      },
    });
  }

  baixarExcelMatriculas(turma: Turma): void {
    const id = turma.id?.trim();
    if (!id) {
      this.toastr.warning('Turma sem identificador.', 'Exportação');
      return;
    }
    this.exportandoTurmaId = id;
    this.turmaService
      .exportarMatriculasExcel(id)
      .pipe(finalize(() => (this.exportandoTurmaId = null)))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `matriculas-turma-${id}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
          this.toastr.success('Planilha baixada.', 'Exportação');
        },
        error: (err) => {
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível exportar a planilha.'), 'Erro');
        },
      });
  }

  onPageChange(newPage: number): void {
    this.page = newPage;
    this.carregarTurmas();
  }

  abrirDialog(): void {
    this.dialogService.open(TurmaFormComponent).onClose.subscribe((resultado) => {
      if (resultado) this.carregarTurmas();
    });
  }

  editar(turma: Turma): void {
    this.dialogService
      .open(TurmaFormComponent, { context: { turma } })
      .onClose.subscribe((resultado) => {
        if (resultado) this.carregarTurmas();
      });
  }

  abrirDetalhes(turma: Turma): void {
    const id = turma.id?.trim();
    if (!id) {
      this.toastr.warning('Turma sem identificador.', 'Detalhes');
      return;
    }
    this.dialogService
      .open(TurmaDetalhesDialogComponent, { context: { turma } })
      .onClose.subscribe((recarregar) => {
        if (recarregar) this.carregarTurmas();
      });
  }

}
